import React, { useEffect, useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock } from 'lucide-react';
import { authenticatedFetch } from '../utils/api';

// Cache pour les mises à jour
let updatesCache = null;
let lastFetchTime = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes en millisecondes

const ProductUpdatesPanel = ({ onClose, position = { top: 60, right: 120 } }) => {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  
  const filteredUpdates = useMemo(() => {
    if (filter === 'all') return updates;
    return updates.filter(update => update.type === filter);
  }, [updates, filter]);

  useEffect(() => {
    const fetchUpdates = async () => {
      try {
        const currentTime = Date.now();
        
        // Utiliser le cache si disponible et pas expiré
        if (updatesCache && (currentTime - lastFetchTime < CACHE_DURATION)) {
          setUpdates(updatesCache);
          setLoading(false);
          return;
        }

        // Sinon, faire un nouvel appel API
        const data = await authenticatedFetch('/changelog/all');
        const newUpdates = Array.isArray(data) ? data : [];
        
        // Mettre à jour le cache
        updatesCache = newUpdates;
        lastFetchTime = currentTime;
        
        setUpdates(newUpdates);
      } catch (err) {
        console.error('Error fetching updates:', err);
        setUpdates([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUpdates();

    // Nettoyage lors du démontage
    return () => {
      // Le cache reste en place, mais on pourrait le nettoyer si nécessaire
    };
  }, []);  
  
    return ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[99999]" onClick={onClose}>
                <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                onClick={e => e.stopPropagation()}
                className="product-updates-panel fixed w-96 bg-surface-secondary/95 rounded-lg shadow-xl border border-border backdrop-blur-xl"
                style={{
                  top: `${position.top}px`,
                  right: `${position.right}px`
                }}
                >
                <div className="flex items-center justify-between p-4 border-b border-border">
                    <h3 className="text-lg font-medium text-ink">Product Updates</h3>
                    <div className="flex items-center gap-2">
                        <select 
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="bg-surface-tertiary text-sm text-ink rounded px-2 py-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.nativeEvent.stopImmediatePropagation();
                        }}
                    >
                    <option value="all">All Updates</option>
                    <option value="feature">Features</option>
                    <option value="fix">Fixes</option>
                    <option value="improvement">Improvements</option>
                    </select>
                    <button onClick={onClose} className="text-ink-secondary hover:text-ink transition-colors">
                    <X size={20} />
                    </button>
                </div>
            </div>
    
            <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
              <AnimatePresence>
                {loading ? (
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="p-4 space-y-3"
                  >
                    {[1, 2, 3].map(i => (
                      <div key={i} className="animate-pulse">
                        <div className="h-4 bg-surface-tertiary rounded w-3/4 mb-2"></div>
                        <div className="h-3 bg-surface-tertiary rounded w-1/2"></div>
                      </div>
                    ))}
                  </motion.div>
                ) : filteredUpdates.length === 0 ? (
                  <div className="p-8 text-center text-ink-secondary">
                    <p>No updates available in this category</p>
                  </div>
                ) : (
                  filteredUpdates.map((update) => (
                    <motion.div
                        key={update.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="p-4 border-b border-border hover:bg-surface-tertiary transition-colors"
                        >
                        <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-1 rounded ${getTypeColor(update.type)}`}>
                                {update.type}
                            </span>
                            <h4 className="text-ink font-medium">
                                {update.title}
                            </h4>
                            </div>
                            <span className="text-xs text-ink-secondary flex items-center gap-1">
                            <Clock size={12} />
                            {new Date(update.date).toLocaleDateString()}
                            </span>
                        </div>
                        <p className="text-sm text-ink text-justify">{update.description}</p>
                        </motion.div>
                  ))
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>,
        document.body
      );
    };
    
    const getTypeColor = (type) => {
      const colors = {
        feature: 'bg-success-soft text-success',
        fix: 'bg-danger-soft text-danger',
        improvement: 'bg-accent-soft text-accent',
      };
      return colors[type] || 'bg-surface-tertiary text-ink-secondary';
    };
    
    export default ProductUpdatesPanel;